import React, { FC, useEffect, useMemo, useState } from 'react';
import { UserDetailPopup } from '../UserDetailsPopup/UserDetailPopup';
import { Field, FieldInputProps, FieldMetaProps, Form, Formik, FormikHelpers } from 'formik';
import { IdentityHookResult } from '../../hooks/useIdentity';
import { USER_DETAIL_POPUP_TYPE } from '../../types/user-detail-popup-type';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/store';
import { Button, Grid, Stack, TextField, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import QuestionMarkIcon from '@mui/icons-material/QuestionMark';
import { HtmlTooltip } from '../General/Reusable/HtmlTooltip';
import { TextFieldError } from '../General/TextFieldError';
import { GreyCheckbox } from '../General/Reusable/GreyCheckbox';
import { UserDetailValidationSchemaBuilder } from '../../util/userDetailValidationBuilder';
import GenderField from './GenderField';
import { getInitialUserDetailValues } from '../../util/formInitialValues';
import { getCountryConfig } from '../../util/getCountryConfig';
import { AddressServiceLanguage } from '../../services/addressService';
import { FormikAddressFrom } from '../Address/FormikAddressFrom';
import LanguageField from './LanguageField';
import { useUserBlockStatus } from '../../hooks/useUserBlockStatus';
import { describeUserBlockStatus } from '../../util/userBlockMessage';
import { useTranslation } from 'react-i18next';

interface UserDetailGridProps {
  identityHook: IdentityHookResult;
  editModeEnabled: boolean;
  setEditMode: React.Dispatch<React.SetStateAction<boolean>>;
  setSavingEditedUserData: React.Dispatch<React.SetStateAction<boolean>>;
}

export const UserDetailGrid: FC<UserDetailGridProps> = ({
  identityHook,
  editModeEnabled,
  setEditMode,
  setSavingEditedUserData,
}) => {
  const { identity, patchIdentity } = identityHook;
  const [country, setCountry] = useState<string>();
  const countryConfig = useMemo(() => {
    return getCountryConfig(country ?? identity?.address?.countryCode ?? '');
  }, [country, identity?.address?.countryCode]);

  const [sendNewVerificationMailPopup, setSendNewVerificationMailPopup] = useState<boolean>(false);
  const [userDetailPopupType, setUserDetailPopupType] = useState<
    USER_DETAIL_POPUP_TYPE | undefined
  >(undefined);
  const { t } = useTranslation();
  const userBlockState = useUserBlockStatus(identity?.id);
  const isSystemBlocked = userBlockState.status === 'blocked';
  // A failed lookup must not render as "not blocked" — the checkbox goes indeterminate instead of staying unchecked.
  const isBlockStatusUnknown = userBlockState.status === 'error';
  const systemBlockedTooltipMessage = useMemo(
    () => describeUserBlockStatus(userBlockState, t),
    [userBlockState, t]
  );
  const [isLinkedWithSpotify, setIsLinkedWithSpotify] = useState<boolean>(false);

  const handleSendVerificationEmail = () => {
    setUserDetailPopupType(USER_DETAIL_POPUP_TYPE.VERIFY_EMAIL_REQUEST);
    setSendNewVerificationMailPopup(true);
  };

  useEffect(() => {
    if (identity?.spotify?.partnerId) {
      setIsLinkedWithSpotify(true);
    } else {
      setIsLinkedWithSpotify(false);
    }
  }, [identity?.spotify?.partnerId]);

  const validationProperties = useSelector((state: RootState) => state.validationProperties);

  const userEditValidationSchema = useMemo(() => {
    return new UserDetailValidationSchemaBuilder(validationProperties)
      .addFirstName()
      .addLastName()
      .addGender()
      .addNickName()
      .addBirthDay()
      .addAddress(AddressServiceLanguage.NL, countryConfig)
      .addTelephone()
      .build();
  }, [validationProperties, countryConfig]);

  const initialIdentityValues = useMemo(() => {
    return getInitialUserDetailValues(identity!);
  }, [identity]);

  const isSocialAccount = useMemo(
    () => identity?.id.startsWith('oauth2|NRCM|') || identity?.id.startsWith('google-oauth2|'),
    [identity]
  );

  const resetFormValues = (actions: FormikHelpers<any>) => {
    const fieldsToReset = {
      'namesPerson.firstName': initialIdentityValues.namesPerson?.firstName,
      'namesPerson.lastName': initialIdentityValues.namesPerson?.lastName,
      'namesPerson.nickName': initialIdentityValues.namesPerson?.nickName,
      'demographicsPerson.genderCode': initialIdentityValues.demographicsPerson?.genderCode,
      'demographicsPerson.genderCustom': initialIdentityValues.demographicsPerson?.genderCustom,
      'demographicsPerson.dateOfBirth': initialIdentityValues.demographicsPerson?.dateOfBirth,
      'address.street': initialIdentityValues.address?.street,
      'address.houseNumberExtension': identity?.address?.houseNumberExtension ?? '',
      'address.city': initialIdentityValues.address?.city,
      'address.countryCode': initialIdentityValues.address?.countryCode,
      'address.postalCode': initialIdentityValues.address?.postalCode,
      'contactpointsPhone.phone': initialIdentityValues.contactpointsPhone?.phone,
      language: initialIdentityValues.language,
    };

    Object.entries(fieldsToReset).forEach(([field, value]) => {
      actions.setFieldValue(field, value);
    });
  };

  return (
    identity && (
      <Formik
        initialValues={initialIdentityValues}
        enableReinitialize
        onSubmit={(values, actions) => {
          setSavingEditedUserData(true);
          patchIdentity({ ...values })
            .then(() => {
              actions.setSubmitting(false);
              setEditMode(false);
            })
            .finally(() => {
              setSavingEditedUserData(false);
            });
        }}
        onReset={(values, actions) => {
          resetFormValues(actions);
          setEditMode(false);
        }}
        validationSchema={userEditValidationSchema}
        validateOnChange={true}>
        {(formik) => {
          return (
            <Form id="userDetailForm">
              <Grid container spacing={1}>
                <Grid
                  item
                  xs={4}
                  sx={{
                    display: 'flex',
                  }}
                  display="flex">
                  <Typography
                    variant="MHCaption"
                    sx={{
                      my: 'auto',
                    }}>
                    Identity ID
                  </Typography>
                </Grid>
                <Grid item xs={8}>
                  <TextField
                    id="identityId"
                    name="identity.id"
                    variant="outlined"
                    size="small"
                    className="white_outlined uneditable"
                    fullWidth
                    value={identity.id}
                    disabled
                    inputProps={{ 'aria-label': 'Identity ID' }}
                  />
                </Grid>
                <Grid
                  item
                  xs={4}
                  sx={{
                    display: 'flex',
                  }}>
                  <Typography
                    variant="MHCaption"
                    sx={{
                      my: 'auto',
                    }}>
                    Email address
                  </Typography>
                </Grid>
                <Grid item xs={8}>
                  <TextField
                    id="email"
                    name="identity.email"
                    variant="outlined"
                    size="small"
                    className="white_outlined uneditable"
                    fullWidth
                    value={identity.email}
                    disabled
                    inputProps={{ 'aria-label': 'Email address' }}
                  />
                </Grid>
                <Grid
                  item
                  xs={4}
                  sx={{
                    display: 'flex',
                  }}>
                  <Typography
                    variant="MHCaption"
                    sx={{
                      my: 'auto',
                    }}>
                    First name
                  </Typography>
                </Grid>
                <Grid item xs={8}>
                  <Field name="namesPerson.firstName">
                    {({
                      field,
                      meta,
                    }: {
                      field: FieldInputProps<string>;
                      meta: FieldMetaProps<string>;
                    }) => (
                      <TextField
                        id="namesPerson.firstName"
                        variant="outlined"
                        size="small"
                        className="white_outlined"
                        fullWidth
                        required={false}
                        label={meta.value ? null : 'First name'}
                        error={!!meta.error}
                        helperText={<TextFieldError error={meta.error} />}
                        disabled={!editModeEnabled}
                        {...field}
                        inputProps={{ 'aria-label': 'First name' }}
                        InputLabelProps={{ shrink: false }}
                      />
                    )}
                  </Field>
                </Grid>
                <Grid
                  item
                  xs={4}
                  sx={{
                    display: 'flex',
                  }}>
                  <Typography
                    variant="MHCaption"
                    sx={{
                      my: 'auto',
                    }}>
                    Last name
                  </Typography>
                </Grid>
                <Grid item xs={8}>
                  <Field name="namesPerson.lastName">
                    {({
                      field,
                      meta,
                    }: {
                      field: FieldInputProps<string>;
                      meta: FieldMetaProps<string>;
                    }) => (
                      <TextField
                        id="namesPerson.lastName"
                        variant="outlined"
                        size="small"
                        className="white_outlined"
                        fullWidth
                        required={false}
                        label={meta.value ? null : 'Last name'}
                        error={!!meta.error}
                        helperText={<TextFieldError error={meta.error} />}
                        disabled={!editModeEnabled}
                        {...field}
                        inputProps={{ 'aria-label': 'Last name' }}
                        InputLabelProps={{ shrink: false }}
                      />
                    )}
                  </Field>
                </Grid>
                <Grid
                  item
                  xs={4}
                  sx={{
                    display: 'flex',
                  }}>
                  <Typography
                    variant="MHCaption"
                    sx={{
                      my: 'auto',
                    }}>
                    Nickname
                  </Typography>
                </Grid>
                <Grid item xs={8}>
                  <Field name="namesPerson.nickName">
                    {({
                      field,
                      meta,
                    }: {
                      field: FieldInputProps<string>;
                      meta: FieldMetaProps<string>;
                    }) => (
                      <TextField
                        id="namesPerson.nickName"
                        variant="outlined"
                        size="small"
                        className="white_outlined"
                        fullWidth
                        required={false}
                        label={meta.value ? null : 'Nickname'}
                        error={!!meta.error}
                        helperText={<TextFieldError error={meta.error} />}
                        disabled={!editModeEnabled}
                        {...field}
                        inputProps={{ 'aria-label': 'Nickname' }}
                        InputLabelProps={{ shrink: false }}
                      />
                    )}
                  </Field>
                </Grid>

                <GenderField
                  id={'demographicsPerson.genderCode'}
                  editModeEnabled={editModeEnabled}
                  formikProps={formik}
                />
                <Grid
                  item
                  xs={4}
                  sx={{
                    display: 'flex',
                  }}>
                  <Typography
                    variant="MHCaption"
                    sx={{
                      my: 'auto',
                    }}>
                    Birthdate
                  </Typography>
                </Grid>
                <Grid item xs={8}>
                  <Field name="demographicsPerson.dateOfBirth">
                    {({
                      field,
                      meta,
                    }: {
                      field: FieldInputProps<string>;
                      meta: FieldMetaProps<string>;
                    }) => (
                      <TextField
                        id="demographicsPerson.dateOfBirth"
                        variant="outlined"
                        size="small"
                        className="white_outlined"
                        fullWidth
                        required={false}
                        label={meta.value ? null : 'Birthdate'}
                        error={!!meta.error}
                        helperText={<TextFieldError error={meta.error} />}
                        disabled={!editModeEnabled}
                        {...field}
                        inputProps={{ 'aria-label': 'Birthdate' }}
                        InputLabelProps={{ shrink: false }}
                      />
                    )}
                  </Field>
                </Grid>
                <Grid
                  item
                  xs={4}
                  sx={{
                    display: 'flex',
                  }}>
                  <Typography
                    variant="MHCaption"
                    sx={{
                      my: 'auto',
                    }}>
                    Phone number
                  </Typography>
                </Grid>
                <Grid item xs={8}>
                  <Field name="contactpointsPhone.phone">
                    {({
                      field,
                      meta,
                    }: {
                      field: FieldInputProps<string>;
                      meta: FieldMetaProps<string>;
                    }) => (
                      <TextField
                        id="contactpointsPhone.phone"
                        variant="outlined"
                        size="small"
                        className="white_outlined"
                        fullWidth
                        required={false}
                        label={meta.value ? null : 'Phone number'}
                        error={!!meta.error}
                        helperText={<TextFieldError error={meta.error} />}
                        disabled={!editModeEnabled}
                        {...field}
                        inputProps={{ 'aria-label': 'Phone number' }}
                        InputLabelProps={{ shrink: false }}
                      />
                    )}
                  </Field>
                </Grid>
                <FormikAddressFrom
                  formikProps={formik}
                  editModeEnabled={
                    false // TODO change to editModeEnabled once BFF points to IMS
                  }
                  onCountryChange={setCountry}
                />

                <Grid
                  item
                  xs={4}
                  sx={{
                    display: 'flex',
                  }}>
                  <Typography
                    variant="MHCaption"
                    sx={{
                      my: 'auto',
                    }}>
                    Identity levels
                  </Typography>
                </Grid>
                <Grid item xs={8}>
                  <Field name="identityLevels">
                    {({
                      field,
                      meta,
                    }: {
                      field: FieldInputProps<string>;
                      meta: FieldMetaProps<string>;
                    }) => (
                      <TextField
                        id="identityLevels"
                        variant="outlined"
                        size="small"
                        className="white_outlined uneditable"
                        fullWidth
                        required={false}
                        label={meta.value ? null : 'Identity levels'}
                        error={!!meta.error}
                        helperText={<TextFieldError error={meta.error} />}
                        disabled
                        {...field}
                        inputProps={{ 'aria-label': 'Identity levels' }}
                        InputLabelProps={{ shrink: false }}
                      />
                    )}
                  </Field>
                </Grid>
                <Grid
                  item
                  xs={4}
                  sx={{
                    display: 'flex',
                  }}>
                  <Typography
                    variant="MHCaption"
                    sx={{
                      my: 'auto',
                    }}>
                    Creation time
                  </Typography>
                </Grid>
                <Grid item xs={8}>
                  <TextField
                    id="createdAt"
                    name="identity.createdAt"
                    variant="outlined"
                    size="small"
                    className="white_outlined uneditable"
                    fullWidth
                    value={
                      identity?.auditInfo?.creationDate &&
                      new Date(identity.auditInfo.creationDate).toLocaleString()
                    }
                    disabled
                    inputProps={{ 'aria-label': 'Creation time' }}
                  />
                </Grid>
                <Grid
                  item
                  xs={4}
                  sx={{
                    display: 'flex',
                  }}>
                  <Typography
                    variant="MHCaption"
                    sx={{
                      my: 'auto',
                    }}>
                    Registered at
                  </Typography>
                </Grid>
                <Grid item xs={8}>
                  <TextField
                    id="registeredAt"
                    name="identity.registeredAt"
                    variant="outlined"
                    size="small"
                    className="white_outlined uneditable"
                    fullWidth
                    value={identity.registeredAt}
                    disabled
                    inputProps={{ 'aria-label': 'Registered at' }}
                  />
                </Grid>
                <Grid
                  item
                  xs={4}
                  sx={{
                    display: 'flex',
                  }}>
                  <Typography
                    variant="MHCaption"
                    sx={{
                      my: 'auto',
                    }}>
                    Language
                  </Typography>
                </Grid>
                <Grid item xs={8}>
                  <LanguageField
                    id={'language'}
                    editModeEnabled={editModeEnabled}
                    formikProps={formik}
                  />
                </Grid>
                <Grid
                  item
                  xs={4}
                  sx={{
                    display: 'flex',
                  }}>
                  <Typography
                    variant="MHCaption"
                    sx={{
                      my: 'auto',
                    }}>
                    Last login
                  </Typography>
                </Grid>
                <Grid item xs={8}>
                  <TextField
                    id="lastLogin"
                    name="identity.lastLogin"
                    variant="outlined"
                    size="small"
                    className="white_outlined uneditable"
                    fullWidth
                    value={identity.lastLogin && new Date(identity.lastLogin).toLocaleString()}
                    disabled
                    inputProps={{ 'aria-label': 'Last login' }}
                  />
                </Grid>
                <Grid
                  item
                  xs={4}
                  sx={{
                    display: 'flex',
                  }}>
                  <Typography
                    variant="MHCaption"
                    sx={{
                      my: 'auto',
                    }}>
                    Verified email
                  </Typography>
                </Grid>
                <Grid item xs={8}>
                  <Stack
                    direction={'row'}
                    spacing={2}
                    sx={{
                      alignItems: 'center',
                    }}>
                    <GreyCheckbox
                      id="verifiedEmail"
                      disabled
                      label="Verified email"
                      name="identity.verifiedEmail"
                      checked={identity.emailVerified}
                    />
                    {!identity.emailVerified && (
                      <Button
                        variant="brandContained"
                        color="brandBlack"
                        size="small"
                        type="button"
                        disabled={isSocialAccount}
                        onClick={() => handleSendVerificationEmail()}>
                        Send new verification mail
                      </Button>
                    )}
                  </Stack>
                </Grid>

                {sendNewVerificationMailPopup && (
                  <UserDetailPopup
                    identityHook={identityHook}
                    showPopup={sendNewVerificationMailPopup}
                    popupClosed={setSendNewVerificationMailPopup}
                    popupType={userDetailPopupType}
                  />
                )}

                <Grid
                  item
                  xs={4}
                  sx={{
                    mt: 0.5,
                  }}>
                  <Typography
                    variant="MHCaption"
                    sx={{
                      my: 'auto',
                    }}>
                    Linked with Spotify
                  </Typography>
                </Grid>
                <Grid
                  item
                  xs={8}
                  sx={{
                    mt: 0.5,
                  }}>
                  <Stack
                    direction="row"
                    spacing={2}
                    sx={{
                      alignItems: 'center',
                    }}>
                    <GreyCheckbox
                      id="spotifylinked"
                      label="Linked"
                      checked={isLinkedWithSpotify}
                      disabled
                    />
                    <HtmlTooltip
                      placement="right"
                      title="This checkbox indicates that the customer has a link to Spotify on our side. If the customer needs to unlink their Spotify account at Spotify, they can visit https://content-access.spotify.com/ ">
                      <InfoOutlinedIcon fontSize="small" fontWeight="small" />
                    </HtmlTooltip>
                  </Stack>
                </Grid>

                <Grid
                  item
                  xs={4}
                  sx={{
                    mt: 0.5,
                  }}>
                  <Typography
                    variant="MHCaption"
                    sx={{
                      my: 'auto',
                    }}>
                    {t('userBlock.label')}
                  </Typography>
                  <Typography
                    variant="MHCaption2"
                    sx={{
                      my: 'auto',
                    }}>
                    {t('userBlock.sublabel')}
                  </Typography>
                </Grid>
                <Grid
                  item
                  xs={8}
                  sx={{
                    mt: 0.5,
                  }}>
                  <Stack
                    direction="row"
                    spacing={2}
                    sx={{
                      alignItems: 'center',
                    }}>
                    <GreyCheckbox
                      id="blocked"
                      checked={isSystemBlocked}
                      indeterminate={isBlockStatusUnknown}
                      indeterminateIcon={<QuestionMarkIcon />}
                      disabled
                      inputProps={{
                        'aria-label': t('userBlock.label') as string,
                        // MUI only sets data-indeterminate; without this a screen reader would announce the failed
                        // lookup as an ordinary unchecked box — the audio version of the bug this state exists to fix.
                        ...(isBlockStatusUnknown ? { 'aria-checked': 'mixed' as const } : {}),
                      }}
                    />
                    <HtmlTooltip placement="right" title={systemBlockedTooltipMessage}>
                      {isBlockStatusUnknown ? (
                        <ErrorOutlineIcon
                          data-testid="block-status-icon"
                          fontSize="small"
                          color="error"
                        />
                      ) : (
                        <InfoOutlinedIcon
                          data-testid="block-status-icon"
                          fontSize="small"
                          fontWeight="small"
                        />
                      )}
                    </HtmlTooltip>
                  </Stack>
                </Grid>
              </Grid>
            </Form>
          );
        }}
      </Formik>
    )
  );
};
